/**
 * Resilient CMS fetch — full pipeline:
 *
 *   1. Read cache (version-checked, TTL-checked). Paint immediately if hit.
 *   2. Check circuit breaker for this URL.
 *      • CLOSED / HALF-OPEN → proceed with fetch + retries.
 *      • OPEN                → fail-fast, keep whatever we already painted.
 *   3. On success: cache + emit source=cms.
 *   4. On total failure: keep cache paint (or fallback baseline) so the
 *      site NEVER goes blank.
 *
 * All thresholds are driven by /app/frontend/src/config/resilience.js.
 * All state transitions emit structured events via lib/cmsTelemetry.js.
 */
import RESILIENCE_CONFIG from "@/config/resilience";
import { emit, CMS_EVENTS } from "@/lib/cmsTelemetry";
import {
  allow as breakerAllow,
  recordSuccess as breakerSuccess,
  recordFailure as breakerFailure,
} from "@/lib/circuitBreaker";
import { read as cacheRead, write as cacheWrite } from "@/lib/cmsCache";

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

async function fetchOnce(url) {
  const r = await fetch(url, { credentials: "omit" });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

async function fetchWithRetry(url, config) {
  const delays = config.retryDelaysMs;
  let lastErr;
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    // Re-check the breaker before every attempt (except the first, which
    // loadCms already gated). Prevents hammering a URL that opened mid-cycle.
    if (attempt > 0 && !breakerAllow(url, config)) {
      throw lastErr || new Error("circuit-breaker-open");
    }
    const start = now();
    emit(CMS_EVENTS.FETCH_START, { url, attempt });
    try {
      const data = await fetchOnce(url);
      emit(CMS_EVENTS.FETCH_SUCCESS, { url, attempt, ms: Math.round(now() - start) });
      breakerSuccess(url);
      return data;
    } catch (err) {
      lastErr = err;
      emit(CMS_EVENTS.FETCH_FAILURE, {
        url, attempt, ms: Math.round(now() - start), error: String(err && err.message || err),
      });
      breakerFailure(url, config);
      const delay = delays[attempt];
      if (delay == null) break;
      await new Promise((res) => setTimeout(res, delay));
    }
  }
  throw lastErr;
}

/**
 * @param {string} url        — CMS endpoint (relative, same-origin).
 * @param {string} cacheKey   — short logical key; the cache namespaces it.
 * @param {*}      fallback   — static baseline (baked into the JS bundle).
 * @param {Function} setState — React setter; receives { data, loading, error, source }.
 * @param {object} [configOverride] — optional per-call overrides (tests).
 */
export function loadCms(url, cacheKey, fallback, setState, configOverride) {
  const config = { ...RESILIENCE_CONFIG, ...(configOverride || {}) };

  // 1. Immediate paint from cache / fallback.
  const cached = cacheRead(cacheKey, config);
  const initialSource = cached ? "cache" : "fallback";
  setState({
    data: cached || fallback,
    loading: true,
    error: null,
    source: initialSource,
  });
  emit(CMS_EVENTS.SOURCE, { url, source: initialSource });

  // 2. Circuit-breaker gate.
  if (!breakerAllow(url, config)) {
    // Fail-fast; publish an error but keep whatever was already painted.
    setState({
      data: cached || fallback,
      loading: false,
      error: new Error("circuit-breaker-open"),
      source: initialSource,
    });
    return () => {};
  }

  // 3. Background refresh with retries. Breaker tracking is now handled
  //    per-attempt inside fetchWithRetry.
  let cancelled = false;
  fetchWithRetry(url, config)
    .then((fresh) => {
      if (cancelled) return;
      cacheWrite(cacheKey, fresh, config);
      setState({ data: fresh, loading: false, error: null, source: "cms" });
      emit(CMS_EVENTS.SOURCE, { url, source: "cms" });
    })
    .catch((err) => {
      if (cancelled) return;
      // Keep whatever we already painted (cache or fallback).
      setState({
        data: cached || fallback,
        loading: false,
        error: err,
        source: initialSource,
      });
    });

  return () => { cancelled = true; };
}
